import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { exist, Id } from '@campaign-portal/namespace/common/id';
import { NamedObject } from '@campaign-portal/namespace/common/namedObject';
import { IdObject } from '@campaign-portal/namespace/common/idObject';
import { SHORT_LINK_PATTERN } from '@helpers/utils/template-message.serializer';
import { TrafficType, WhatsappButtonType } from '@campaign-portal/namespace/common/enums';
import { ShortLinkChannelsMap } from '../../pages/campaigns/campaign-wizard/campaign-wizard.service';
import { BehaviorSubject } from 'rxjs';
import { TemplateWhatsappActionButtonControls } from '../../pages/campaigns/message-templates/templates/update-template/interfaces/message-template-form.interfaces';

export const URL_REGEXP = /^(https?):\/\/([\w.-]+)(:\d+)?(\/[\w./-]*)?(\?[^\s#]*)?(#\S*)?$/;
export class CPCustomValidators {
	static readonly URL_REGEXP = URL_REGEXP;

	static hasTemplateDuplicate<T extends IdObject<exist> & NamedObject>(id: Id, entities: T[]): ValidatorFn {
		return (control: AbstractControl<string>): ValidationErrors | null => {
			const duplicate = entities.find((entity) => control.value === entity.name && id !== entity.id);
			return duplicate ? { duplicate: true } : null;
		};
	}

	static shortLinkLimit(): ValidatorFn {
		return (control: AbstractControl<string | null>): ValidationErrors | null => {
			const shortLinksAmount = control.value?.match(SHORT_LINK_PATTERN())?.length ?? 0;
			return shortLinksAmount > 1 ? { shortLinksLimit: true } : null;
		};
	}

	static sameShortLink(channel: TrafficType, map$: BehaviorSubject<ShortLinkChannelsMap>): ValidatorFn {
		return (): ValidationErrors | null => {
			const currentLink = map$.value.get(channel);
			if (!currentLink) {
				return null;
			}

			const hasAnotherLink = [...map$.value.entries()].find(([comparedChannel, comparedLink]) => {
				return comparedChannel !== channel && comparedLink && comparedLink.full !== currentLink.full;
			});
			return hasAnotherLink ? { sameShortLink: true } : null;
		};
	}

	static recipientsLimitAndPattern(max: number, splitPattern: RegExp): ValidatorFn {
		return (control: AbstractControl<string>): ValidationErrors | null => {
			const contacts = control.value.split(splitPattern);
			const error = contacts.find((contact) => {
				const match = /^\d+$/.test(contact);
				return !match;
			});
			if (error) {
				return { pattern: true };
			}
			return contacts.length > max ? { limitExceeded: true } : null;
		};
	}

	static whatsappButtonsDuplicate(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const formArray = control as FormArray;
			const formGroups = formArray.controls as FormGroup<TemplateWhatsappActionButtonControls>[];

			const valueMap = new Map<unknown, AbstractControl[]>();
			formGroups.forEach((group) => {
				const { name, type } = group.controls;
				delete name.errors?.nameDuplicate;
				const value = name.value;
				if (!value || type.value === WhatsappButtonType.COPY_CODE) {
					return;
				}
				if (valueMap.has(value)) {
					valueMap.get(value)!.push(name);
				} else {
					valueMap.set(value, [name]);
				}
			});
			this.setDuplicateErrors(valueMap, { nameDuplicate: true });
			return null;
		};
	}

	static requiredSomeControls(controlNames: string[]): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const formGroup = control as FormGroup;
			const controls = controlNames
				.map((name) => formGroup?.get(name))
				.filter((control) => control?.enabled) as AbstractControl[];
			if (controls.length) {
				const hasFilled = controls.some((control) => Validators.required(control) === null);
				return hasFilled ? null : { required: true };
			}
			return null;
		};
	}

	static urlWithProtocol(): ValidatorFn {
		return (control: AbstractControl<string>): ValidationErrors | null => {
			if (control.value) {
				return control.value.includes('://') ? null : { urlWithProtocol: true };
			}
			return null;
		};
	}

	private static setDuplicateErrors(
		controlsMap: Map<unknown, AbstractControl[]>,
		duplicateErrors: ValidationErrors
	): void {
		controlsMap.forEach((controls) => {
			const isDuplicate = controls.length > 1;
			controls.forEach((control) => {
				const errors = isDuplicate ? { ...control.errors, ...duplicateErrors } : control.errors;
				const hasErrors = Object.keys(errors ?? {}).length > 0;
				control.setErrors(hasErrors ? errors : null);
			});
		});
	}
}
